import React, { useState } from 'react';

/**
 * GA4 API Help Modal - Internal documentation
 */
export default function ApiHelp() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('metrics');

  const metrics = {
    'Základní': [
      { name: 'sessions', desc: 'Počet session' },
      { name: 'activeUsers', desc: 'Aktivní uživatelé' },
      { name: 'newUsers', desc: 'Noví uživatelé' },
      { name: 'eventCount', desc: 'Počet událostí' }
    ],
    'Konverze': [
      { name: 'conversions', desc: 'Celkový počet konverzí' },
      { name: 'engagedSessions', desc: 'Angažované session' },
      { name: 'bounceRate', desc: 'Míra okamžitého opuštění' }
    ],
    'Obsah': [
      { name: 'screenPageViews', desc: 'Zobrazení stránek' },
      { name: 'averageSessionDuration', desc: 'Průměrná doba session' }
    ],
    'Příjmy': [
      { name: 'purchaseRevenue', desc: 'Příjmy z nákupů' },
      { name: 'itemRevenue', desc: 'Příjmy položek' },
      { name: 'itemQuantity', desc: 'Počet prodaných položek' }
    ]
  };

  const dimensions = {
    'Zdroje': [
      { name: 'sessionSource', desc: 'Zdroj session (Google, Facebook, ...)' },
      { name: 'sessionMedium', desc: 'Médium session (organic, cpc, banner, ...)' },
      { name: 'sessionCampaignName', desc: 'Název kampaně' },
      { name: 'defaultChannelGroup', desc: 'Kanál (Direct, Organic Search, Paid Search, ...)' }
    ],
    'Obsah': [
      { name: 'pagePath', desc: 'URL cesta stránky' },
      { name: 'pageTitle', desc: 'Název stránky' },
      { name: 'eventName', desc: 'Název události (purchase, add_to_cart, ...)' }
    ],
    'Zařízení': [
      { name: 'deviceCategory', desc: 'Typ zařízení (Desktop, Mobile, Tablet)' },
      { name: 'platform', desc: 'Platforma (web, iOS, Android)' },
      { name: 'operatingSystem', desc: 'Operační systém' }
    ],
    'Geografie': [
      { name: 'country', desc: 'Země' },
      { name: 'city', desc: 'Město' },
      { name: 'region', desc: 'Region' }
    ],
    'Čas': [
      { name: 'date', desc: 'Datum (YYYYMMDD)' },
      { name: 'hour', desc: 'Hodina (00-23)' },
      { name: 'dayOfWeekName', desc: 'Den v týdnu' }
    ]
  };

  const examples = [
    { q: 'Kolik bylo sessions tento týden?', desc: 'Základní metrika za časové období' },
    { q: 'Jak fungují bannery tento měsíc?', desc: 'Analýza konkrétního média (UTM Intelligence)' },
    { q: 'Které kampaně mají nejvíc konverzí?', desc: 'Top kampaně podle konverzí' },
    { q: 'Porovnej mobile vs desktop za posledních 14 dní', desc: 'Srovnání podle zařízení' },
    { q: 'Zobraz mi top 10 stránek podle zobrazení', desc: 'Analýza obsahu' },
    { q: 'Kolik bylo nákupů včera?', desc: 'Specifická událost za období' }
  ];

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="p-2 rounded-full hover:bg-surface-light dark:hover:bg-surface-dark transition-colors"
        title="Nápověda k GA4 API"
      >
        <span className="material-symbols-outlined text-muted-light dark:text-muted-dark">
          help
        </span>
      </button>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={() => setIsOpen(false)}
      />

      {/* Modal */}
      <div className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-4xl md:max-h-[90vh] bg-surface-light dark:bg-surface-dark rounded-lg shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-light dark:border-border-dark">
          <h2 className="text-xl font-bold text-text-light dark:text-text-dark flex items-center gap-2">
            <span className="material-symbols-outlined text-primary">help</span>
            GA4 API Nápověda
          </h2>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 rounded-full hover:bg-background-light dark:hover:bg-background-dark"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border-light dark:border-border-dark">
          <button
            onClick={() => setActiveTab('metrics')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'metrics'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-light dark:text-muted-dark hover:text-text-light dark:hover:text-text-dark'
            }`}
          >
            📊 Metriky
          </button>
          <button
            onClick={() => setActiveTab('dimensions')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'dimensions'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-light dark:text-muted-dark hover:text-text-light dark:hover:text-text-dark'
            }`}
          >
            📋 Dimenze
          </button>
          <button
            onClick={() => setActiveTab('examples')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'examples'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-light dark:text-muted-dark hover:text-text-light dark:hover:text-text-dark'
            }`}
          >
            💡 Příklady
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'metrics' && (
            <div className="space-y-6">
              {Object.entries(metrics).map(([category, items]) => (
                <div key={category}>
                  <h3 className="text-lg font-semibold text-text-light dark:text-text-dark mb-3">
                    {category}
                  </h3>
                  <div className="grid gap-2">
                    {items.map((item) => (
                      <div
                        key={item.name}
                        className="flex items-start gap-3 p-3 bg-background-light dark:bg-background-dark rounded-lg"
                      >
                        <code className="text-sm font-mono text-primary px-2 py-1 bg-primary-light dark:bg-primary-dark rounded flex-shrink-0">
                          {item.name}
                        </code>
                        <span className="text-sm text-text-light dark:text-text-dark">
                          {item.desc}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'dimensions' && (
            <div className="space-y-6">
              {Object.entries(dimensions).map(([category, items]) => (
                <div key={category}>
                  <h3 className="text-lg font-semibold text-text-light dark:text-text-dark mb-3">
                    {category}
                  </h3>
                  <div className="grid gap-2">
                    {items.map((item) => (
                      <div
                        key={item.name}
                        className="flex items-start gap-3 p-3 bg-background-light dark:bg-background-dark rounded-lg"
                      >
                        <code className="text-sm font-mono text-primary px-2 py-1 bg-primary-light dark:bg-primary-dark rounded flex-shrink-0">
                          {item.name}
                        </code>
                        <span className="text-sm text-text-light dark:text-text-dark">
                          {item.desc}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'examples' && (
            <div className="space-y-3">
              <p className="text-sm text-muted-light dark:text-muted-dark mb-4">
                Můžete se ptát přirozeně v češtině - AI automaticky převede dotaz na správné GA4 API parametry.
              </p>
              {examples.map((ex, idx) => (
                <div
                  key={idx}
                  className="p-4 bg-background-light dark:bg-background-dark rounded-lg border-l-4 border-primary"
                >
                  <div className="font-medium text-text-light dark:text-text-dark mb-1">
                    "{ex.q}"
                  </div>
                  <div className="text-sm text-muted-light dark:text-muted-dark">
                    {ex.desc}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border-light dark:border-border-dark text-sm text-muted-light dark:text-muted-dark">
          💡 Tip: Chatbot podporuje kontext - můžete pokračovat v konverzaci (např. "A co na mobilu?")
        </div>
      </div>
    </>
  );
}
